import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { LoadingSpinner } from '../../components/layout/SharedComponents';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Shield, FileText, Users, MessageSquare, Award, TrendingDown, Clock, ArrowRight, Download,
} from 'lucide-react';

const TYPE_ICONS = { audit: Shield, state: ArrowRight, document: FileText, clarification: MessageSquare, supplier: Users };
const TYPE_COLORS = { audit: 'text-blue-600 bg-blue-50', state: 'text-indigo-600 bg-indigo-50', document: 'text-violet-600 bg-violet-50', clarification: 'text-amber-600 bg-amber-50', supplier: 'text-emerald-600 bg-emerald-50' };

export function AuditTrailTab({ eventRef }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get(`/api/rfx/workflow/audit/${eventRef}`);
        setData(r.data);
      } catch { toast.error('Failed to load audit trail'); }
      setLoading(false);
    };
    load();
  }, [eventRef]);

  const exportAuditCSV = () => {
    if (!data?.timeline?.length) return;
    const headers = ['Timestamp', 'Type', 'Action', 'Detail', 'Actor'];
    const rows = data.timeline.map(t => [
      t.at, t.type, `"${(t.action || '').replace(/"/g, '""')}"`, `"${(t.detail || '').replace(/"/g, '""')}"`, t.actor,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `Audit_${eventRef}_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success('Audit trail exported');
  };

  if (loading) return <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>;
  if (!data) return <p className="text-sm text-slate-400 py-8 text-center">Unable to load audit trail.</p>;

  const filtered = filter === 'all' ? data.timeline : data.timeline.filter(t => t.type === filter);
  const filters = [
    { id: 'all', label: 'All' },
    { id: 'state', label: 'State Changes' },
    { id: 'supplier', label: 'Suppliers' },
    { id: 'document', label: 'Documents' },
    { id: 'clarification', label: 'Q&A' },
    { id: 'audit', label: 'System' },
  ];

  return (
    <div className="space-y-4 py-2" data-testid="audit-trail-tab">
      {/* Summary */}
      <div className="grid grid-cols-5 gap-2">
        <div className="border rounded-lg p-2.5 text-center bg-white">
          <p className="text-lg font-bold text-slate-700">{data.timeline?.length || 0}</p>
          <p className="text-[10px] text-slate-500">Total Actions</p>
        </div>
        <div className="border rounded-lg p-2.5 text-center bg-white">
          <p className="text-lg font-bold text-indigo-700">{data.status_history?.length || 0}</p>
          <p className="text-[10px] text-slate-500">State Changes</p>
        </div>
        <div className="border rounded-lg p-2.5 text-center bg-white">
          <p className="text-lg font-bold text-emerald-700">{data.supplier_participation?.length || 0}</p>
          <p className="text-[10px] text-slate-500">Suppliers</p>
        </div>
        <div className="border rounded-lg p-2.5 text-center bg-white">
          <p className="text-lg font-bold text-violet-700">{data.document_changes?.length || 0}</p>
          <p className="text-[10px] text-slate-500">Doc Changes</p>
        </div>
        <div className="border rounded-lg p-2.5 text-center bg-white">
          <p className="text-lg font-bold text-amber-700">{data.clarifications?.length || 0}</p>
          <p className="text-[10px] text-slate-500">Clarifications</p>
        </div>
      </div>

      {/* Award decision summary */}
      {data.award_decision && (
        <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-800">Award Decision</span>
            <span className="text-xs text-emerald-600 ml-auto">{data.award_decision.awarded_at}</span>
          </div>
          <p className="text-xs text-emerald-700">
            Awarded to: {(data.award_decision.awards || []).map(a => a.supplier_name || a.supplier_code).join(', ')}
            {data.award_decision.notes && ` — ${data.award_decision.notes}`}
          </p>
        </div>
      )}

      {/* Linked downstream */}
      {(data.linked_requisitions?.length > 0 || data.linked_purchase_orders?.length > 0) && (
        <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
          <p className="text-sm font-semibold text-blue-800 mb-1">Downstream Traceability</p>
          <div className="flex gap-4 text-xs">
            {data.linked_requisitions?.map(r => (
              <span key={r.request_number} className="bg-white px-2 py-1 rounded border">
                <FileText className="w-3 h-3 inline mr-1" />{r.request_number} <Badge className="text-[8px] ml-1">{r.status}</Badge>
              </span>
            ))}
            {data.linked_purchase_orders?.map(po => (
              <span key={po.order_number} className="bg-white px-2 py-1 rounded border">
                <FileText className="w-3 h-3 inline mr-1" />{po.order_number} <Badge className="text-[8px] ml-1">{po.status}</Badge>
              </span>
            ))}
          </div>
          {data.converted_from && <p className="text-xs text-blue-600 mt-1">Converted from: {data.converted_from}</p>}
        </div>
      )}

      {/* Filter + Export */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {filters.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 text-xs rounded-full transition-colors ${filter === f.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={exportAuditCSV} data-testid="export-audit-btn">
          <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Timeline */}
      <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-6">No activity recorded.</p>
        ) : filtered.map((item, i) => {
          const Icon = TYPE_ICONS[item.type] || Clock;
          const colorCls = TYPE_COLORS[item.type] || 'text-slate-600 bg-slate-50';
          return (
            <div key={i} className="flex gap-3 px-4 py-2.5 hover:bg-slate-50/50" data-testid={`audit-entry-${i}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${colorCls}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-800">{item.action}</p>
                {item.detail && <p className="text-[11px] text-slate-500 truncate">{item.detail}</p>}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[10px] text-slate-400">{item.actor}</p>
                <p className="text-[10px] text-slate-400">{item.at ? new Date(item.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
